import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle2, Calendar, FileText, Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdmissionPage = () => {
  const [formData, setFormData] = useState({
    studentName: '',
    dateOfBirth: '',
    className: '',
    parentName: '',
    mobile: '',
    email: '',
    address: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [admissionSettings, setAdmissionSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdmissionSettings();
  }, []);

  const fetchAdmissionSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admission-settings`);
      setAdmissionSettings(response.data);
    } catch (error) {
      console.error('Error fetching admission settings:', error);
      setAdmissionSettings(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Admission form submitted:', formData);
    setSubmitted(true);
    toast.success('Application submitted successfully! We will contact you soon.');
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setFormData({
        studentName: '',
        dateOfBirth: '',
        className: '',
        parentName: '',
        mobile: '',
        email: '',
        address: ''
      });
      setSubmitted(false);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative h-96 bg-gray-900">
        <img
          src="https://images.unsplash.com/photo-1701302550962-d807b97100fa"
          alt="Students with backpacks"
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/40 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Admissions Open for {admissionSettings?.session_year || '2026-27'}
            </h1>
            <p className="text-xl text-gray-200">
              Join AMTEE Public School - Building Bright Futures
            </p>
          </div>
        </div>
      </div>

      {/* Admission Process */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
            Admission Process
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="shadow-lg">
              <CardHeader>
                <div className="w-16 h-16 bg-[#4A90E2]/10 rounded-full flex items-center justify-center mb-4">
                  <FileText className="h-8 w-8 text-[#4A90E2]" />
                </div>
                <CardTitle>1. Fill Application</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Complete the online application form or visit school office to collect the form.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardHeader>
                <div className="w-16 h-16 bg-[#4A90E2]/10 rounded-full flex items-center justify-center mb-4">
                  <Users className="h-8 w-8 text-[#4A90E2]" />
                </div>
                <CardTitle>2. Interview</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Parents and student will be called for a brief interaction with the admission committee.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="shadow-lg">
              <CardHeader>
                <div className="w-16 h-16 bg-[#4A90E2]/10 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-8 w-8 text-[#4A90E2]" />
                </div>
                <CardTitle>3. Confirmation</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>
                  Upon selection, complete the admission formalities and fee payment to confirm admission.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">Online Admission Application</CardTitle>
              <CardDescription>
                Fill in the details below and we will contact you shortly
              </CardDescription>
            </CardHeader>
            <CardContent>
              {submitted ? (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <AlertDescription className="text-green-800 font-medium">
                    Application submitted successfully! We will contact you within 2-3 working days.
                  </AlertDescription>
                </Alert>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="studentName">Student Name *</Label>
                      <Input
                        id="studentName"
                        name="studentName"
                        value={formData.studentName}
                        onChange={handleChange}
                        required
                        placeholder="Enter student name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                      <Input
                        id="dateOfBirth"
                        name="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="className">Applying for Class *</Label>
                    <Input
                      id="className"
                      name="className"
                      value={formData.className}
                      onChange={handleChange}
                      required
                      placeholder="e.g., Class 5, Class 8"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="parentName">Parent/Guardian Name *</Label>
                      <Input
                        id="parentName"
                        name="parentName"
                        value={formData.parentName}
                        onChange={handleChange}
                        required
                        placeholder="Enter parent name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mobile">Mobile Number *</Label>
                      <Input
                        id="mobile"
                        name="mobile"
                        type="tel"
                        value={formData.mobile}
                        onChange={handleChange}
                        required
                        placeholder="10-digit mobile number"
                        pattern="[0-9]{10}"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address">Address *</Label>
                    <Textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      placeholder="Enter complete address"
                      rows={3}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#4A90E2] hover:bg-[#357ABD] text-lg py-6"
                  >
                    Submit Application
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Important Dates */}
          <Card className="mt-8 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-6 w-6 text-[#4A90E2]" />
                Important Dates
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {loading ? (
                <div className="text-center py-4 text-gray-500">Loading dates...</div>
              ) : admissionSettings ? (
                <>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="font-medium">Application Start Date</span>
                    <span className="text-gray-600">{formatDate(admissionSettings.start_date)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b">
                    <span className="font-medium">Application Last Date</span>
                    <span className="text-gray-600">{formatDate(admissionSettings.end_date)}</span>
                  </div>
                  {admissionSettings.session_start_date && (
                    <div className="flex justify-between items-center py-2">
                      <span className="font-medium">Session Starts</span>
                      <span className="text-gray-600">{formatDate(admissionSettings.session_start_date)}</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500 text-sm">Dates not available</p>
                  <p className="text-xs text-gray-400 mt-1">Please contact school administration</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdmissionPage;
